import { Failure } from '@mrhenry/failure';
import { html, TemplateResult } from 'lit';
import { live } from 'lit/directives/live.js';
import { classMap } from 'lit/directives/class-map.js';
import { Dispatcher } from '../../../types/booking-request';
import { formatDate, isDateInputSupported, parseDate } from '../../../helpers/date';
import { monthAndYearForPropertyDataRequest, PropertyDataUpdateLatch } from '../../../api/get-calendar-data-for-house';
import { WeekData } from '../../../types/booking-data';
import { staticText } from '../../../static-text';

export function startOfStayInput(
	startOfStay: Date | null,
	houseId: number,
	weekData: WeekData | null,
	propertyDataUpdateLatch: PropertyDataUpdateLatch,
	dispatch: Dispatcher,
	assetUrl: string,
	startOfStayAria?: string,
	startOfStayFailure?: Failure
): TemplateResult {
	const firstSaturday = new Date();
	// Saturdays is 6.
	if ( 6 !== firstSaturday.getUTCDay() ) {
		// Add to the current date the extra days until Saturday.
		firstSaturday.setUTCDate( firstSaturday.getUTCDate() + ( 7 - ( ( firstSaturday.getUTCDay() + 1 ) % 7 ) ) );
	}

	// 10 years from now, last day of year.
	const tenYearsFromNow = new Date();
	tenYearsFromNow.setUTCFullYear( tenYearsFromNow.getUTCFullYear() + 10 );
	tenYearsFromNow.setUTCMonth( 11 );
	tenYearsFromNow.setUTCDate( 31 );

	const minDate = formatDate( firstSaturday );
	const maxDate = formatDate( tenYearsFromNow );

	const changeHandler = async( e: InputEvent ) => {
		if ( e.target !== e.currentTarget ) {
			return;
		}

		if ( !( e.target instanceof HTMLInputElement ) ) {
			throw new Error( 'e.target should be instanceof HTMLInputElement' );
		}

		let value : Date | null = null;

		// Browser who do not support date inputs won't have a solid valueAsDate.
		// We need to convert it manually.
		// We expect the user to give in the format in a specific format (yyyy-mm-dd)
		if ( isDateInputSupported() ) {
			value = e.target.valueAsDate;
		} else {
			value = parseDate( e.target.value );
		}

		if ( !value ) {
			dispatch( {
				type: 'UPDATE_START_OF_STAY',
				startOfStay: value,
				startOfStayFailure: Failure.from( staticText.startOfStayInValidFormatFailureMessage ),
			} );

			return;
		}

		if ( !e.target.checkValidity() ) {
			dispatch( {
				type: 'UPDATE_START_OF_STAY',
				startOfStay: value,
				startOfStayFailure: Failure.from( staticText.startOfStayInValidFailureMessage ),
			} );

			return;
		}

		if ( value < new Date() ) {
			dispatch( {
				type: 'UPDATE_START_OF_STAY',
				startOfStay: value,
				startOfStayFailure: Failure.from( staticText.startOfStayInNotInTheFutureFailureMessage ),
			} );

			return;
		}

		// Check if date is a saturday, modify it if has to:
		let aria;
		if ( 6 !== value.getUTCDay() ) {
			value.setUTCDate( value.getUTCDate() + ( 7 - ( ( value.getUTCDay() + 1 ) % 7 ) ) );
			aria = staticText.startOfStaySaturdayToSaturdayMessage;
		}

		const monthAndYear = monthAndYearForPropertyDataRequest( value );
		propertyDataUpdateLatch.setInput( houseId, monthAndYear.year, monthAndYear.month );

		dispatch( {
			type: 'UPDATE_START_OF_STAY_AND_FOCUS_DATE',
			date: value,
			startOfStayAria: aria,
		} );
	};

	const errorMessageHTML: TemplateResult[] = [];
	if ( Failure.isFailure( startOfStayFailure ) ) {
		errorMessageHTML.push( html`
			<p class="form__field-validation-error theme-color-error">
				<img alt="" class="form__field-validation-error__icon" src="${assetUrl}/assets/images/alert-circle.svg">
				${startOfStayFailure?.message}
			</p>
		` );
	}

	let ariaLive = 'off';
	const aria: TemplateResult[] = [];
	if ( startOfStayAria !== undefined ) {
		ariaLive = 'polite';

		aria.push( html`
			<p class="form__field-validation-feedback theme-color-orange">
				${startOfStayAria}
			</p>
		` );
	}

	// Adding error and aria messages here if available
	if ( startOfStay && weekData ) {
		const currentWeek = weekData.weeks.find( ( week ) => {
			return (
				week.date === startOfStay.getUTCDate() &&
				week.month === startOfStay.getUTCMonth() + 1 &&
				week.year === startOfStay.getUTCFullYear()
			);
		} );

		if ( currentWeek ) {
			if ( !currentWeek.priceInEuroCents || currentWeek.availability === 'NOT_AVAILABLE' ) {
				errorMessageHTML.push( html`
					<p class="form__field-validation-error theme-color-error">
						<img alt="" class="form__field-validation-error__icon" src="${assetUrl}/assets/images/alert-circle.svg">
						${staticText.startOfStayDateIsNotAvailable}
					</p>
				` );

			} else {
				switch ( currentWeek.availability ) {
					case 'IS_BOOKED':
						errorMessageHTML.push( html`
							<p class="form__field-validation-error theme-color-error">
								<img alt="" class="form__field-validation-error__icon" src="${assetUrl}/assets/images/alert-circle.svg">
								${staticText.startOfStayDateIsBooked}
							</p>
						` );

						break;
					case 'HAS_OPTION':
						ariaLive = 'polite';
						aria.push( html`
							<p class="form__field-validation-feedback theme-color-orange">
								${staticText.startOfStayDateHasAnOption}
							</p>
						` );

						break;
					case 'AVAILABLE':
						break;
					default:
						break;
				}
			}
		}
	}

	const classes = {
		'has-error': 0 < errorMessageHTML.length,
		'has-feedback': 0 < aria.length,
	};

	let currentValue: string | null = null;
	if ( startOfStay ) {
		currentValue = formatDate( startOfStay );
	}

	return html`
		<div class="form__row ${classMap( classes )}">
			<div class="form__label-container type-d">
				<label class="form__label" for="start-of-stay">
					${staticText.yourSelection}
				</label>
			</div>

			<input
				?disabled=${propertyDataUpdateLatch.loading}
				.value=${live( currentValue )}
				@change=${changeHandler}
				class="form__input"
				id="start-of-stay"
				min=${minDate}
				max="${maxDate}"
				name="start-of-stay"
				placeholder="yyyy-mm-dd"
				step="7"
				type="date"
				pattern="20[0-9][0-9]-[0-1][0-9]-[0-3][0-9]"
			/>

			<div aria-live=${live( ariaLive )}>
				${aria}
			</div>

			${errorMessageHTML}
		</div>
	`;
}
